import { useCallback, useEffect, useRef, useState } from 'react';
import { CLOUD_PAUSE_MS, chooseEngine, chunkForSpeech, detectSpeechLanguage, pickVoice, toSpeakableText } from '@/lib/speechLogic';
import { checkCloudVoice, fetchSpeechAudio, playBlob } from '@/lib/cloudSpeech';

// One "no voice for this language" notice per language, and one cloud-fallback notice, per page session.
const warned = new Set<string>();
let cloudNoticeShown = false;
// Cloud attempts pause for CLOUD_PAUSE_MS after a failure; module-level so a remount does not retry at once.
let cloudPausedUntil = 0;

interface Options {
  /** Tenant UI language; Latin replies in a Latin-script UI language are spoken in it. */
  uiLanguage: string;
  onNoVoice?: (language: string) => void;
  /** Called once per session when the cloud voice failed and the on-device voice took over. */
  onCloudFallback?: () => void;
}

/**
 * Reads text aloud. A tenant that opted in to cloud voice gets the provider's voice for the languages it covers;
 * everything else, and every cloud failure, uses the browser's on-device voices only (no network voices).
 * One playback at a time; replacing or stopping it is immediate.
 */
export function useSpeech({ uiLanguage, onNoVoice, onCloudFallback }: Options) {
  const [deviceAvailable] = useState(
    () => typeof window !== 'undefined' && 'speechSynthesis' in window && typeof SpeechSynthesisUtterance !== 'undefined',
  );
  const [cloudLanguages, setCloudLanguages] = useState<string[]>([]);
  const [speakingIndex, setSpeakingIndex] = useState<number | null>(null);
  const tokenRef = useRef(0);
  const voicesRef = useRef<SpeechSynthesisVoice[]>([]);
  const utteranceRef = useRef<SpeechSynthesisUtterance | null>(null);
  const playbackRef = useRef<{ cancel: () => void } | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const mountedRef = useRef(false);
  const cbRef = useRef({ uiLanguage, onNoVoice, onCloudFallback });
  cbRef.current = { uiLanguage, onNoVoice, onCloudFallback };
  const cloudLanguagesRef = useRef(cloudLanguages);
  cloudLanguagesRef.current = cloudLanguages;
  const available = deviceAvailable || cloudLanguages.length > 0;

  // Which languages the tenant's cloud voice covers (empty unless an admin enabled and configured it).
  useEffect(() => {
    let cancelled = false;
    void checkCloudVoice().then((langs) => { if (!cancelled) setCloudLanguages(langs); });
    return () => { cancelled = true; };
  }, []);

  // Voices often load after first render (Chrome), so keep the list fresh.
  useEffect(() => {
    if (!deviceAvailable) return;
    const synth = window.speechSynthesis;
    const load = () => { voicesRef.current = synth.getVoices(); };
    load();
    synth.addEventListener?.('voiceschanged', load);
    return () => { synth.removeEventListener?.('voiceschanged', load); };
  }, [deviceAvailable]);

  // On unmount: no speech may start or continue (send() can resolve after the chat is gone).
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      // The refs are intentionally read live here: they must reflect the latest playback at unmount.
      // eslint-disable-next-line react-hooks/exhaustive-deps
      tokenRef.current++;
      abortRef.current?.abort();
      playbackRef.current?.cancel();
      try { window.speechSynthesis?.cancel(); } catch { /* nothing playing */ }
    };
  }, []);

  // Ends whatever is playing (device or cloud) and invalidates its callbacks.
  const halt = useCallback(() => {
    tokenRef.current++;
    utteranceRef.current = null;
    abortRef.current?.abort();
    abortRef.current = null;
    playbackRef.current?.cancel();
    playbackRef.current = null;
    try { window.speechSynthesis?.cancel(); } catch { /* nothing playing */ }
  }, []);

  const stop = useCallback(() => {
    halt();
    setSpeakingIndex(null);
  }, [halt]);

  const speak = useCallback((index: number, text: string) => {
    if (!mountedRef.current || (!deviceAvailable && cloudLanguagesRef.current.length === 0)) return;
    const chunks = chunkForSpeech(toSpeakableText(text));
    halt();
    const token = tokenRef.current;
    if (!chunks.length) {
      setSpeakingIndex(null);
      return;
    }
    const language = detectSpeechLanguage(text, cbRef.current.uiLanguage);
    const alive = () => token === tokenRef.current;
    const finish = () => {
      if (!alive()) return;
      utteranceRef.current = null;
      setSpeakingIndex(null);
    };

    // On-device path, from chunk `from`. Local voices only; with none for this language we say so rather than use a default (possibly network) voice.
    // Returns true when speech started with a local voice, false on the no-voice path.
    const playDevice = (from: number): boolean => {
      const synth = deviceAvailable ? window.speechSynthesis : null;
      let voices = voicesRef.current;
      if (synth && voices.length === 0) {
        // Voices may not have loaded into the ref yet: ask the engine once before deciding.
        try { voices = synth.getVoices(); } catch { voices = []; }
      }
      const voice = synth ? pickVoice(voices, language) : null;
      if (!synth || !voice) {
        setSpeakingIndex(null);
        if (!warned.has(language)) {
          warned.add(language);
          cbRef.current.onNoVoice?.(language);
        }
        return false;
      }
      setSpeakingIndex(index);
      const playFrom = (i: number) => {
        if (!alive()) return;
        if (i >= chunks.length) { finish(); return; }
        const u = new SpeechSynthesisUtterance(chunks[i]);
        u.lang = voice.lang;
        u.voice = voice;
        u.onend = () => playFrom(i + 1);
        u.onerror = finish;
        // Keep a reference: Chrome can garbage-collect a playing utterance and silently drop its events.
        utteranceRef.current = u;
        try { synth.speak(u); } catch { finish(); }
      };
      playFrom(from);
      return true;
    };

    // Any cloud failure: pause cloud attempts and continue the rest of the reply on the device voice.
    // The cloud-fallback notice is shown (once) only when the device actually started speaking; with no
    // local voice, only the on-device "no voice" notice runs.
    const fallBackToDevice = (from: number) => {
      cloudPausedUntil = Date.now() + CLOUD_PAUSE_MS;
      if (!alive()) return;
      if (playDevice(from) && !cloudNoticeShown) {
        cloudNoticeShown = true;
        cbRef.current.onCloudFallback?.();
      }
    };

    // Cloud path: fetch chunk i+1 while chunk i plays.
    const playCloud = async () => {
      const ctrl = new AbortController();
      abortRef.current = ctrl;
      setSpeakingIndex(index);
      const prefetch = (i: number): Promise<Blob> | null => {
        if (i >= chunks.length) return null;
        const p = fetchSpeechAudio(chunks[i], language, ctrl.signal);
        p.catch(() => undefined); // handled when awaited; avoids an unhandled-rejection report while a chunk plays
        return p;
      };
      let pending = prefetch(0);
      for (let i = 0; i < chunks.length; i++) {
        let blob: Blob;
        try { blob = await pending!; } catch { if (alive()) fallBackToDevice(i); return; }
        if (!alive()) return;
        pending = prefetch(i + 1);
        try {
          await playBlob(blob, playbackRef);
        } catch {
          // Cancel the already-started prefetch(i + 1): the rest of the reply goes to the device voice.
          ctrl.abort();
          if (abortRef.current === ctrl) abortRef.current = null;
          if (alive()) fallBackToDevice(i);
          return;
        }
        if (!alive()) return;
      }
      finish();
    };

    const engine = chooseEngine({
      cloudLanguages: cloudLanguagesRef.current, language, pausedUntil: cloudPausedUntil, now: Date.now(),
    });
    if (engine === 'cloud') void playCloud();
    else playDevice(0);
  }, [deviceAvailable, halt]);

  return { available, speakingIndex, speak, stop };
}
